import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { RemixBreadcrumbs } from '#app/components/navigation/remix-breadcrumbs.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { CodeBlock } from '#app/old/components/data-display/code-block.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Source, type componentProps, Features, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'RemixBreadcrumbs',
}

const component: componentProps = {
	name: 'RemixBreadcrumbs',
	shortName: 'RemixBreadcrumbs',
	fileName: 'remix-breadcrumbs',
	category: 'navigation',
	description: ['A hierarchy of links to the current page in an application, specifically for Remix.'],
	features: ['Automatically generates breadcrumbs based on the current route.', 'The last link is automatically marked as the current page using aria-current.'],
	parts: [],
	usage: `<RemixBreadcrumbs />`,
	styling: ``,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const BreadcrumbsRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, features, usage, shortName, fileName } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<RemixBreadcrumbs />
			<Features features={features} />
			<H2>Usage Note</H2>
			<P>
				To include a route in the Breadcrumbs, add a <Code>breadcrumb</Code> attribute to your route's <Code>handle</Code>. Customize the attribute value to suit your needs.
			</P>
			<CodeBlock
				code={`export const handle = {
	breadcrumb: 'Docs',
}`}
				filename="docs"
				extension="tsx"
			/>
			<P>
				You have complete control over which routes are included in the <Code>Breadcrumbs</Code> and you can customize the breadcrumb text for each route.
			</P>
			<Source source={source} fileName={fileName} />
		</>
	)
}
export default BreadcrumbsRoute
